import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ChevronRight, ChevronLeft, Building2, Car, Receipt, CheckCircle, BookOpen, Sparkles } from 'lucide-react';
import { taxConfigApi, taxRatesApi } from '../services/api';

const STATES = [
  { code: 'AL', name: 'Alabama', incomeTax: 5.0 },
  { code: 'AK', name: 'Alaska', incomeTax: 0 },
  { code: 'AZ', name: 'Arizona', incomeTax: 2.5 },
  { code: 'AR', name: 'Arkansas', incomeTax: 4.7 },
  { code: 'CA', name: 'California', incomeTax: 13.3 },
  { code: 'CO', name: 'Colorado', incomeTax: 4.4 },
  { code: 'CT', name: 'Connecticut', incomeTax: 6.99 },
  { code: 'DE', name: 'Delaware', incomeTax: 6.6 },
  { code: 'FL', name: 'Florida', incomeTax: 0 },
  { code: 'GA', name: 'Georgia', incomeTax: 5.49 },
  { code: 'HI', name: 'Hawaii', incomeTax: 11.0 },
  { code: 'ID', name: 'Idaho', incomeTax: 5.8 },
  { code: 'IL', name: 'Illinois', incomeTax: 4.95 },
  { code: 'IN', name: 'Indiana', incomeTax: 3.05 },
  { code: 'IA', name: 'Iowa', incomeTax: 5.7 },
  { code: 'KS', name: 'Kansas', incomeTax: 5.7 },
  { code: 'KY', name: 'Kentucky', incomeTax: 4.5 },
  { code: 'LA', name: 'Louisiana', incomeTax: 4.25 },
  { code: 'ME', name: 'Maine', incomeTax: 7.15 },
  { code: 'MD', name: 'Maryland', incomeTax: 5.75 },
  { code: 'MA', name: 'Massachusetts', incomeTax: 5.0 },
  { code: 'MI', name: 'Michigan', incomeTax: 4.25 },
  { code: 'MN', name: 'Minnesota', incomeTax: 9.85 },
  { code: 'MS', name: 'Mississippi', incomeTax: 5.0 },
  { code: 'MO', name: 'Missouri', incomeTax: 4.95 },
  { code: 'MT', name: 'Montana', incomeTax: 6.75 },
  { code: 'NE', name: 'Nebraska', incomeTax: 6.64 },
  { code: 'NV', name: 'Nevada', incomeTax: 0 },
  { code: 'NH', name: 'New Hampshire', incomeTax: 0 },
  { code: 'NJ', name: 'New Jersey', incomeTax: 10.75 },
  { code: 'NM', name: 'New Mexico', incomeTax: 5.9 },
  { code: 'NY', name: 'New York', incomeTax: 10.9 },
  { code: 'NC', name: 'North Carolina', incomeTax: 4.75 },
  { code: 'ND', name: 'North Dakota', incomeTax: 2.5 },
  { code: 'OH', name: 'Ohio', incomeTax: 3.99 },
  { code: 'OK', name: 'Oklahoma', incomeTax: 4.75 },
  { code: 'OR', name: 'Oregon', incomeTax: 9.9 },
  { code: 'PA', name: 'Pennsylvania', incomeTax: 3.07 },
  { code: 'RI', name: 'Rhode Island', incomeTax: 5.99 },
  { code: 'SC', name: 'South Carolina', incomeTax: 6.4 },
  { code: 'SD', name: 'South Dakota', incomeTax: 0 },
  { code: 'TN', name: 'Tennessee', incomeTax: 0 },
  { code: 'TX', name: 'Texas', incomeTax: 0 },
  { code: 'UT', name: 'Utah', incomeTax: 4.65 },
  { code: 'VT', name: 'Vermont', incomeTax: 8.75 },
  { code: 'VA', name: 'Virginia', incomeTax: 5.75 },
  { code: 'WA', name: 'Washington', incomeTax: 0 },
  { code: 'WV', name: 'West Virginia', incomeTax: 6.5 },
  { code: 'WI', name: 'Wisconsin', incomeTax: 7.65 },
  { code: 'WY', name: 'Wyoming', incomeTax: 0 },
];

const ENTITY_TYPES = [
  { value: 'SoleProprietor', label: 'Sole Proprietor', description: 'Most common for single-owner businesses' },
  { value: 'LLC_Single', label: 'Single-Member LLC', description: 'LLC with one owner, taxed as sole proprietor' },
  { value: 'LLC_Multi', label: 'Multi-Member LLC', description: 'LLC with multiple owners, taxed as partnership' },
  { value: 'SCorp', label: 'S Corporation', description: 'Pass-through taxation with payroll requirements' },
  { value: 'CCorp', label: 'C Corporation', description: 'Separate tax entity, double taxation possible' },
];

const VEHICLE_METHODS = [
  { 
    value: 'StandardMileage', 
    label: 'Standard Mileage Rate', 
    description: '70¢ per mile (2025). Simpler tracking, just log miles.' 
  },
  { 
    value: 'ActualExpense', 
    label: 'Actual Expenses', 
    description: 'Track gas, repairs, insurance, depreciation. May yield higher deduction.' 
  },
];

// Getting Started Guide preview steps
const GUIDE_PREVIEW_STEPS = [
  { number: 1, title: 'Set up business profile' },
  { number: 2, title: 'Configure tax settings', completed: true },
  { number: 3, title: 'Add your first customer' },
  { number: 4, title: 'Set up service types' },
  { number: 5, title: 'Create a job' },
  { number: 6, title: 'Complete the job' },
  { number: 7, title: 'Create an invoice' },
  { number: 8, title: 'Get paid!' },
  { number: 9, title: 'Customer portal (optional)' },
];

const TaxSetupWizard = ({ isOpen, onClose, onComplete }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  
  // County dropdown state
  const [counties, setCounties] = useState([]);
  const [loadingCounties, setLoadingCounties] = useState(false);
  
  const [config, setConfig] = useState({
    state: '',
    county: '',
    stateIncomeTaxRate: 0,
    entityType: 'SoleProprietor',
    vehicleExpenseMethod: 'StandardMileage',
    vehicleBusinessUsePercent: 100,
    collectsSalesTax: false,
    salesTaxRate: 0,
    salesTaxId: '',
    salesTaxFilingFrequency: 'Quarterly',
    isSetupComplete: false,
    enableQuarterlyTaxReminders: true,
    enableSalesTaxReminders: true,
    reminderDaysBefore: 14,
  });

  // Load existing configuration
  useEffect(() => {
    if (isOpen) {
      loadExistingConfig();
    }
  }, [isOpen]);

  const loadExistingConfig = async () => {
    try {
      const response = await taxConfigApi.getConfiguration();
      if (response.data && response.data.state) {
        setConfig(prev => ({
          ...prev,
          ...response.data,
        }));
        // Load counties for existing state
        if (response.data.state) {
          loadCounties(response.data.state);
        }
      }
    } catch (error) {
      console.error('Error loading tax config:', error);
    }
  };

  const loadCounties = async (stateCode) => {
    if (!stateCode) {
      setCounties([]);
      return;
    }
    
    try {
      setLoadingCounties(true);
      const response = await taxRatesApi.getCounties(stateCode);
      setCounties(response.data || []);
    } catch (error) {
      console.error('Error loading counties:', error);
      setCounties([]);
    } finally {
      setLoadingCounties(false);
    }
  };

  const handleStateChange = (stateCode) => {
    const state = STATES.find(s => s.code === stateCode);
    setConfig(prev => ({
      ...prev,
      state: stateCode,
      county: '', // Reset county when state changes
      salesTaxRate: 0, // Reset sales tax rate
      stateIncomeTaxRate: state?.incomeTax || 0,
    }));
    
    // Load counties for new state
    loadCounties(stateCode);
  };

  const handleCountyChange = async (countyName) => {
    setConfig(prev => ({ ...prev, county: countyName }));
    
    // Auto-fill sales tax rate from lookup
    if (config.state && countyName) {
      try {
        const response = await taxRatesApi.lookup(config.state, countyName);
        if (response.data && response.data.rate > 0) {
          setConfig(prev => ({
            ...prev,
            county: countyName,
            salesTaxRate: response.data.ratePercent || 0,
            collectsSalesTax: true, // Auto-enable if we found a rate
          }));
        }
      } catch (error) {
        console.error('Error looking up tax rate:', error);
      }
    }
  };

  const handleSkip = async () => {
    try {
      await taxConfigApi.dismiss();
      onClose();
    } catch (error) {
      console.error('Error dismissing wizard:', error);
      onClose();
    }
  };

  const handleSave = async (complete = false) => {
    try {
      setSaving(true);
      await taxConfigApi.saveConfiguration({
        ...config,
        isSetupComplete: complete,
      });
      
      if (complete) {
        // Mark getting started guide as viewed if they saw it
        localStorage.setItem('suddyflow_viewed_getting_started', 'true');
        onComplete?.();
      }
      onClose();
    } catch (error) {
      console.error('Error saving tax config:', error);
      alert('Failed to save tax configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleViewFullGuide = async () => {
    // Save config first, then navigate to guide
    try {
      setSaving(true);
      await taxConfigApi.saveConfiguration({
        ...config,
        isSetupComplete: true,
      });
      localStorage.setItem('suddyflow_viewed_getting_started', 'true');
      onComplete?.();
      onClose();
      navigate('/help/getting-started');
    } catch (error) {
      console.error('Error saving before navigation:', error);
      // Still navigate even if save fails
      onClose();
      navigate('/help/getting-started');
    } finally {
      setSaving(false);
    }
  };

  const totalSteps = 5;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {step === 5 ? 'Getting Started' : 'Tax Setup'}
            </h2>
            <p className="text-sm text-gray-500">Step {step} of {totalSteps}</p>
          </div>
          <button
            onClick={handleSkip}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={24} />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-6 pt-4">
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map((s) => (
              <div
                key={s}
                className={`flex-1 h-2 rounded-full ${
                  s <= step ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 200px)' }}>
          {/* Step 1: Business Location */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Building2 className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Business Location</h3>
                  <p className="text-sm text-gray-500">Where is your business based?</p>
                </div>
              </div>

              <div>
                <label className="label">State *</label>
                <select
                  value={config.state}
                  onChange={(e) => handleStateChange(e.target.value)}
                  className="input"
                >
                  <option value="">Select your state</option>
                  {STATES.map((state) => (
                    <option key={state.code} value={state.code}>
                      {state.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="label">County</label>
                {loadingCounties ? (
                  <div className="input bg-gray-50 text-gray-500">Loading counties...</div>
                ) : counties.length > 0 ? (
                  <select
                    value={config.county}
                    onChange={(e) => handleCountyChange(e.target.value)}
                    className="input"
                  >
                    <option value="">Select your county</option>
                    {counties.map((county) => (
                      <option key={county.county} value={county.county}>
                        {county.county} ({county.ratePercent.toFixed(2)}% sales tax)
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={config.county}
                    onChange={(e) => setConfig({ ...config, county: e.target.value })}
                    className="input"
                    placeholder="Enter your county"
                  />
                )}
                <p className="text-xs text-gray-500 mt-1">
                  {counties.length > 0 
                    ? 'Select to auto-fill sales tax rate' 
                    : config.state 
                      ? 'County rates not available for this state - enter manually'
                      : 'Select a state first'}
                </p>
              </div>

              {config.state && (
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>{STATES.find(s => s.code === config.state)?.name}</strong> has a 
                    {config.stateIncomeTaxRate > 0 
                      ? ` ${config.stateIncomeTaxRate}% top marginal income tax rate`
                      : ' no state income tax'
                    }
                  </p>
                  {config.county && config.salesTaxRate > 0 && (
                    <p className="text-sm text-blue-800 mt-2">
                      <strong>{config.county}</strong> has a {config.salesTaxRate.toFixed(2)}% combined sales tax rate
                    </p>
                  )}
                </div>
              )}

              <div>
                <label className="label">Business Entity Type *</label>
                <div className="space-y-2">
                  {ENTITY_TYPES.map((type) => (
                    <label
                      key={type.value}
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        config.entityType === type.value
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="entityType"
                        value={type.value}
                        checked={config.entityType === type.value}
                        onChange={(e) => setConfig({ ...config, entityType: e.target.value })}
                        className="mt-1"
                      />
                      <div>
                        <p className="font-medium text-gray-900">{type.label}</p>
                        <p className="text-sm text-gray-500">{type.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Vehicle Expenses */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Car className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Vehicle Expenses</h3>
                  <p className="text-sm text-gray-500">How do you want to track vehicle deductions?</p>
                </div>
              </div>

              <div className="space-y-3">
                {VEHICLE_METHODS.map((method) => (
                  <label
                    key={method.value}
                    className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                      config.vehicleExpenseMethod === method.value
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="vehicleMethod"
                      value={method.value}
                      checked={config.vehicleExpenseMethod === method.value}
                      onChange={(e) => setConfig({ ...config, vehicleExpenseMethod: e.target.value })}
                      className="mt-1"
                    />
                    <div>
                      <p className="font-medium text-gray-900">{method.label}</p>
                      <p className="text-sm text-gray-500">{method.description}</p>
                    </div>
                  </label>
                ))}
              </div>

              <div>
                <label className="label">Business Use Percentage</label>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={config.vehicleBusinessUsePercent}
                    onChange={(e) => setConfig({ ...config, vehicleBusinessUsePercent: parseInt(e.target.value) })}
                    className="flex-1"
                  />
                  <span className="text-lg font-semibold w-16 text-right">
                    {config.vehicleBusinessUsePercent}%
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  What percentage of your vehicle use is for business?
                </p>
              </div>

              <div className="bg-amber-50 rounded-lg p-4">
                <p className="text-sm text-amber-800">
                  <strong>Tip:</strong> Keep a mileage log! The IRS requires documentation of business vs. personal miles.
                </p>
              </div>
            </div>
          )}

          {/* Step 3: Sales Tax */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Receipt className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Sales Tax</h3>
                  <p className="text-sm text-gray-500">Do you collect sales tax from customers?</p>
                </div>
              </div>

              <label className="flex items-center gap-3 p-4 rounded-lg border cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.collectsSalesTax}
                  onChange={(e) => setConfig({ ...config, collectsSalesTax: e.target.checked })}
                  className="w-5 h-5 text-blue-600 rounded"
                />
                <div>
                  <p className="font-medium text-gray-900">I collect sales tax</p>
                  <p className="text-sm text-gray-500">
                    Check this if you charge sales tax on your services
                  </p>
                </div>
              </label>

              {config.collectsSalesTax && (
                <div className="space-y-4 pl-4 border-l-2 border-blue-200">
                  <div>
                    <label className="label">Sales Tax Rate (%)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={config.salesTaxRate}
                      onChange={(e) => setConfig({ ...config, salesTaxRate: parseFloat(e.target.value) || 0 })}
                      className="input"
                      placeholder="e.g., 7.25"
                    />
                    {config.county && counties.length > 0 && (
                      <p className="text-xs text-green-600 mt-1">
                        ✓ Auto-filled from {config.county} county rate
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="label">Sales Tax ID (Optional)</label>
                    <input
                      type="text"
                      value={config.salesTaxId}
                      onChange={(e) => setConfig({ ...config, salesTaxId: e.target.value })}
                      className="input"
                      placeholder="Your state sales tax ID"
                    />
                  </div>

                  <div>
                    <label className="label">Filing Frequency</label>
                    <select
                      value={config.salesTaxFilingFrequency}
                      onChange={(e) => setConfig({ ...config, salesTaxFilingFrequency: e.target.value })}
                      className="input"
                    >
                      <option value="Monthly">Monthly</option>
                      <option value="Quarterly">Quarterly</option>
                      <option value="Annually">Annually</option>
                    </select>
                  </div>
                </div>
              )}

              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">
                  <strong>Note:</strong> Sales tax requirements vary by state and service type. 
                  Many states don't require sales tax on certain services. 
                  Check with your state's department of revenue.
                </p>
              </div>
            </div>
          )}

          {/* Step 4: Review */}
          {step === 4 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Review Your Settings</h3>
                  <p className="text-sm text-gray-500">Make sure everything looks correct</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">Business Location</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <span className="text-gray-500">State:</span>
                    <span>{STATES.find(s => s.code === config.state)?.name || 'Not set'}</span>
                    <span className="text-gray-500">County:</span>
                    <span>{config.county || 'Not set'}</span>
                    <span className="text-gray-500">Entity Type:</span>
                    <span>{ENTITY_TYPES.find(t => t.value === config.entityType)?.label}</span>
                    <span className="text-gray-500">State Income Tax:</span>
                    <span>{config.stateIncomeTaxRate}%</span>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">Vehicle Expenses</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <span className="text-gray-500">Method:</span>
                    <span>{VEHICLE_METHODS.find(m => m.value === config.vehicleExpenseMethod)?.label}</span>
                    <span className="text-gray-500">Business Use:</span>
                    <span>{config.vehicleBusinessUsePercent}%</span>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">Sales Tax</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <span className="text-gray-500">Collects Sales Tax:</span>
                    <span>{config.collectsSalesTax ? 'Yes' : 'No'}</span>
                    {config.collectsSalesTax && (
                      <>
                        <span className="text-gray-500">Rate:</span>
                        <span>{config.salesTaxRate}%</span>
                        <span className="text-gray-500">Filing:</span>
                        <span>{config.salesTaxFilingFrequency}</span>
                      </>
                    )}
                  </div>
                </div>

                {/* Reminder Preferences */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-3">Tax Reminders</h4>
                  <div className="space-y-3">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={config.enableQuarterlyTaxReminders}
                        onChange={(e) => setConfig({ ...config, enableQuarterlyTaxReminders: e.target.checked })}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Quarterly estimated tax reminders</p>
                        <p className="text-xs text-gray-500">Remind me before federal quarterly payments are due</p>
                      </div>
                    </label>

                    {config.collectsSalesTax && (
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={config.enableSalesTaxReminders}
                          onChange={(e) => setConfig({ ...config, enableSalesTaxReminders: e.target.checked })}
                          className="w-4 h-4 text-blue-600 rounded"
                        />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Sales tax filing reminders</p>
                          <p className="text-xs text-gray-500">Remind me before sales tax returns are due</p>
                        </div>
                      </label>
                    )}

                    <div className="pt-2">
                      <label className="text-sm text-gray-600">Remind me this many days before:</label>
                      <select
                        value={config.reminderDaysBefore}
                        onChange={(e) => setConfig({ ...config, reminderDaysBefore: parseInt(e.target.value) })}
                        className="input mt-1 w-32"
                      >
                        <option value={7}>7 days</option>
                        <option value={14}>14 days</option>
                        <option value={21}>21 days</option>
                        <option value={30}>30 days</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  You can change these settings anytime from the Settings page.
                </p>
              </div>
            </div>
          )}

          {/* Step 5: Getting Started Guide */}
          {step === 5 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <BookOpen className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">You're Almost Ready!</h3>
                  <p className="text-sm text-gray-500">Here's how to send your first invoice</p>
                </div>
              </div>

              {/* Quick workflow preview */}
              <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-5 border border-purple-200">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="h-5 w-5 text-purple-600" />
                  <h4 className="font-semibold text-gray-900">9 Steps to Your First Invoice</h4>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {GUIDE_PREVIEW_STEPS.map((item) => (
                    <div 
                      key={item.number}
                      className={`flex items-center gap-2 p-2 rounded-lg ${
                        item.completed 
                          ? 'bg-green-100 border border-green-200' 
                          : 'bg-white border border-gray-200'
                      }`}
                    >
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        item.completed 
                          ? 'bg-green-500 text-white' 
                          : 'bg-purple-200 text-purple-700'
                      }`}>
                        {item.completed ? '✓' : item.number}
                      </span>
                      <span className={`text-sm ${item.completed ? 'text-green-800' : 'text-gray-700'}`}>
                        {item.title}
                      </span>
                    </div>
                  ))}
                </div>

                <p className="text-sm text-gray-600 mt-4">
                  You've already completed step 2! The full guide walks you through each step in detail.
                </p>
              </div>

              {/* What you'll learn */}
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <h4 className="font-medium text-gray-900 mb-3">The Guide Covers:</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>How to add customers and create jobs</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Using your pre-loaded service types</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Creating and sending invoices</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Recording payments and tracking revenue</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Setting up customer portal access</span>
                  </li>
                </ul>
              </div>

              {/* Action buttons */}
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-3">
                  You can access this guide anytime from <strong>Help → Getting Started</strong>
                </p>
                <button
                  onClick={handleViewFullGuide}
                  disabled={saving}
                  className="w-full sm:w-auto btn btn-secondary flex items-center justify-center gap-2"
                >
                  <BookOpen size={18} />
                  View Full Guide Now
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <button
            onClick={handleSkip}
            className="text-gray-500 hover:text-gray-700 text-sm"
          >
            Skip for now
          </button>

          <div className="flex gap-3">
            {step > 1 && (
              <button
                onClick={() => setStep(step - 1)}
                className="btn btn-secondary flex items-center gap-1"
              >
                <ChevronLeft size={18} />
                Back
              </button>
            )}

            {step < totalSteps ? (
              <button
                onClick={() => setStep(step + 1)}
                disabled={step === 1 && !config.state}
                className="btn btn-primary flex items-center gap-1"
              >
                Next
                <ChevronRight size={18} />
              </button>
            ) : (
              <button
                onClick={() => handleSave(true)}
                disabled={saving}
                className="btn btn-primary flex items-center gap-1"
              >
                {saving ? 'Saving...' : 'Complete Setup'}
                <CheckCircle size={18} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaxSetupWizard;